import React, { useEffect, useState } from "react";
import { Loading } from "@src/common/components";
import { useGetPackagesPaginatedLazyQuery } from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";
import { PackageEventsDisplay } from "./PackageEventsDisplay";
import { PackageDisplayItem, PackageEventDisplayItem } from "./types";

interface PackageEventsContainerProps {
  shipmentId: string;
}

export function PackageEventsContainer({ shipmentId }: PackageEventsContainerProps) {
  const { courierId } = useAuth();
  const { showToast } = useCourieStore();
  const [packages, setPackages] = useState<PackageDisplayItem[]>([]);

  const [getPackages, { loading }] = useGetPackagesPaginatedLazyQuery({
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (!courierId || !shipmentId) return;

    getPackages({
      variables: {
        courierId,
        shipmentIds: [shipmentId],
      },
    })
      .then((result) => {
        const transformedPackages: PackageDisplayItem[] =
          result.data?.packagesPaginated?.edges?.map((edge) => {
            const pkg = edge?.node;
            if (!pkg) return null;

            const events: PackageEventDisplayItem[] =
              pkg.events?.map((event) => {
                const driverName = event.eventByDriver
                  ? `${event.eventByDriver.firstName} ${event.eventByDriver.lastName}`.trim()
                  : null;

                return {
                  id: event.id,
                  eventTypeName: event.eventTypeName,
                  action: event.action,
                  packageStatus: event.packageStatus,
                  createdAt: event.createdAt,
                  deviceTime: event.deviceTime || null,
                  scannedByDriverName: driverName,
                  location: event.lngLat
                    ? { lat: event.lngLat.lat ?? 0, lng: event.lngLat.lng ?? 0 }
                    : null,
                };
              }) || [];

            return {
              id: pkg.id,
              reference: pkg.reference,
              status: pkg.status,
              events,
            };
          }).filter((pkg): pkg is PackageDisplayItem => pkg !== null) || [];

        setPackages(transformedPackages);
      })
      .catch((error) => showErrorToast(error, showToast));
  }, [courierId, shipmentId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loading loading={true} />
      </div>
    );
  }

  return <PackageEventsDisplay packages={packages} />;
}

